import {AbstractService} from 'services/AbstractService';
import iconPointerClick from 'assets/icons/pointer-click.svg';
import iconCursorClick from 'assets/icons/cursor-click.svg';
import iconGrowingChart from 'assets/icons/growing-chart.svg';
import iconCallToAction from 'assets/icons/call-to-action.svg';
import iconTarget from 'assets/icons/target-icon.svg';
import iconStrategyHeatmap from 'assets/icons/strategy-heatmap.svg';
import uniformDistribution from 'assets/icons/uniform-distribution.svg';
import translation from 'translation';

const BID_MANAGER_AUTOSTRATEGIES_URL = '/api/projects/{0}/bidmanager/autostrategies';

export const HEATMAP_ORDERS_AUTOSTRATEGY_IDS = [
  'HEATMAP_ORDERS_OZON_PAY_PER_CLICK_AD',
  'SELLERS_HEATMAP_ORDERS_OZON_PAY_PER_CLICK_AD'
];

/*
 * Стратегия «Конкурентные ставки» существует в двух вариантах: с оптимизацией ставки по ДРР и заказам и без неё.
 * Вариант выбирается переключателем в форме стратегии и выражается идентификатором автостратегии, а не отдельным
 * полем в её параметрах. Префикс идентификатора зависит от скоупа, в котором создана группа.
 */
const COMPETITIVE_BIDDING_OPTIMIZATION_PAIRS = {
  COMPETITIVE_BIDDING_OZON_PAY_PER_CLICK_AD:
    'COMPETITIVE_BIDDING_NO_OPTIMIZATION_OZON_PAY_PER_CLICK_AD',
  TRMCORE_COMPETITIVE_BIDDING_OZON_PAY_PER_CLICK_AD:
    'TRMCORE_COMPETITIVE_BIDDING_NO_OPTIMIZATION_OZON_PAY_PER_CLICK_AD',
  SELLERS_COMPETITIVE_BIDDING_OZON_PAY_PER_CLICK_AD:
    'SELLERS_COMPETITIVE_BIDDING_NO_OPTIMIZATION_OZON_PAY_PER_CLICK_AD'
};

const COMPETITIVE_BIDDING_OPTIMIZED_IDS = Object.keys(COMPETITIVE_BIDDING_OPTIMIZATION_PAIRS);

export const COMPETITIVE_BIDDING_NO_OPTIMIZATION_AUTOSTRATEGY_IDS = COMPETITIVE_BIDDING_OPTIMIZED_IDS.
  map(optimizedId => COMPETITIVE_BIDDING_OPTIMIZATION_PAIRS[optimizedId]);

const COMPETITIVE_BIDDING_NO_OPTIMIZATION_ALIASES = COMPETITIVE_BIDDING_OPTIMIZED_IDS.
  reduce((aliases, optimizedId) => {
    aliases[COMPETITIVE_BIDDING_OPTIMIZATION_PAIRS[optimizedId]] = optimizedId;
    return aliases;
  }, {});

const COMPETITIVE_BIDDING_PAIRED_IDS = COMPETITIVE_BIDDING_OPTIMIZED_IDS.reduce((paired, optimizedId) => {
  const notOptimizedId = COMPETITIVE_BIDDING_OPTIMIZATION_PAIRS[optimizedId];
  paired[optimizedId] = notOptimizedId;
  paired[notOptimizedId] = optimizedId;
  return paired;
}, {});

export function isCompetitiveBiddingAutostrategyId (autostrategyId) {
  return Object.prototype.hasOwnProperty.call(COMPETITIVE_BIDDING_PAIRED_IDS, autostrategyId);
}

export function isCompetitiveBiddingOptimizationEnabled (autostrategyId) {
  return COMPETITIVE_BIDDING_OPTIMIZED_IDS.includes(autostrategyId);
}

export function getCompetitiveBiddingPairedAutostrategyId (autostrategyId) {
  return COMPETITIVE_BIDDING_PAIRED_IDS[autostrategyId] || null;
}

export class BidManagerAutostrategiesService extends AbstractService {
  _autostrategiesDataForEnrich () {
    return {
      DECREASE_GOAL_CPA: {
        icon: iconPointerClick,
        name: translation.trc(
          'bidmanager',
          'CPA reduction on goal'
        ),
        description: translation.trc(
          'bidmanager',
          'You will receive the lowest possible cost per action, within a given range'
        )
      },
      MAX_TRAFFIC_HOLD_CPC: {
        icon: iconCursorClick,
        name: translation.trc(
          'bidmanager',
          'Maximum traffic while holding CPC'
        ),
        description: translation.trc(
          'bidmanager',
          'You will receive the maximum number of visitors with a fixed value of CPC'
        )
      },
      INCREASE_GOAL_COMPLETIONS: {
        icon: iconGrowingChart,
        name: translation.trc(
          'bidmanager',
          'The increase of the number of goal actions'
        ),
        description: translation.trc(
          'bidmanager',
          'You will receive a maximum of goal actions, taking into account the restrictions you set'
        )
      },
      DECREASE_JOIN_RATE_COST_VKONTAKTE: {
        icon: iconCallToAction,
        name: translation.trc(
          'bidmanager',
          'Reducing the cost of group join'
        ),
        description: translation.trc(
          'bidmanager',
          'You will get control and cost management of group joins on VKontakte'
        )
      },
      UNIFORM_DAY_BUDGET: {
        icon: uniformDistribution,
        name: translation.trc(
          'bidmanager',
          'Uniform spending of the daily budget'
        ),
        description: translation.trc(
          'bidmanager',
          'You will get smooth budget spending within the specified time'
        )
      },
      HEATMAP_ORDERS_OZON_PAY_PER_CLICK_AD: {
        icon: iconStrategyHeatmap,
        label: {text: translation.trc('bidmanager', 'NEW'), order: 1},
        name: translation.trc(
          'bidmanager',
          'More orders with heatmap'
        ),
        description: translation.trc(
          'bidmanager',
          'Bids increase during the most profitable hours, while optimal CRR and cost per order are calculated. For products with 5+ orders a week.'
        )
      },
      SELLERS_HEATMAP_ORDERS_OZON_PAY_PER_CLICK_AD: {
        icon: iconStrategyHeatmap,
        label: {text: translation.trc('bidmanager', 'NEW'), order: 1},
        name: translation.trc(
          'bidmanager',
          'More orders with heatmap'
        ),
        description: translation.trc(
          'bidmanager',
          'Bids increase during the most profitable hours, while optimal CRR and cost per order are calculated. For products with 5+ orders a week.'
        )
      },
      HOLD_TARGET_CRR_OZON_PAY_PER_CLICK_AD: {
        label: {text: translation.trc('bidmanager', 'RECOMMENDED'), order: 0},
        icon: iconCallToAction,
        nameSeller: 'Удержание ДРР',
        name: translation.trc(
          'bidmanager',
          'Hold target CRR'
        ),
        description: translation.trc(
          'bidmanager',
          'You will receive a maximum number of orders, taking into account CRR you set'
        ),
        descriptionSeller: 'Реклама будет лучше окупаться из-за перераспределения бюджета на более рентабельные товары'
      },
      HOLD_TARGET_ROI_OZON_PAY_PER_CLICK_AD: {
        icon: iconCursorClick,
        name: translation.trc(
          'bidmanager',
          'Optimization for targeted ROI'
        ),
        nameSeller: 'Оптимизация по ROI',
        description: translation.trc(
          'bidmanager',
          'You will improve advertising return on investment within specified constraints'
        ),
        descriptionSeller: 'Получите максимум заказов c учётом желаемого показателя ROI'
      },
      INCREASE_ROI_HOLD_CPO_OZON_PAY_PER_CLICK_AD: {
        icon: iconGrowingChart,
        name: translation.trc(
          'bidmanager',
          'Increasing ROI while maintaining CPO'
        ),
        nameSeller: 'Удержание СРО',
        description: translation.trc(
          'bidmanager',
          'You will receive the maximum number of orders for profitable goods and reduce costs on goods with ' +
          'a high CPO'
        ),
        descriptionSeller: 'Получите максимум заказов c учётом желаемого показателя CPO'
      },
      HOLD_TARGET_POSITION_WB_AD_MANUAL: {
        icon: iconCursorClick,
        name: translation.trc(
          'bidmanager',
          'Hold target position'
        ),
        description: translation.trc(
          'bidmanager',
          'You will get the maximum number of impressions within the specified range'
        ),
        descriptionSeller: 'Товар получит максимальное количество показов в желаемом диапазоне позиций'
      },
      HOLD_TARGET_POSITION_WB_AD_UNIFIED: {
        icon: iconCursorClick,
        name: translation.trc(
          'bidmanager',
          'Hold target position'
        ),
        description: translation.trc(
          'bidmanager',
          'You will get the maximum number of impressions within the specified range'
        ),
        descriptionSeller: 'Товар получит максимальное количество показов в желаемом диапазоне позиций'
      },
      MAXIMUM_SALES_ON_TOP_POSITIONS_WB_AD_MANUAL: {
        label: {text: translation.trc('bidmanager', 'RECOMMENDED'), order: 0},
        icon: iconGrowingChart,
        name: translation.trc(
          'bidmanager',
          'Maximum Sales on Top Positions'
        ),
        description: translation.trc(
          'bidmanager',
          'You will be shown in the top positions in search results to achieve the maximum number of sales'
        ),
        descriptionSeller: 'Товар окажется на лучших позициях в поиске по самому целевому кластеру'
      },
      MAXIMUM_SALES_ON_TOP_POSITIONS_WB_AD_UNIFIED: {
        label: {text: translation.trc('bidmanager', 'RECOMMENDED'), order: 0},
        icon: iconGrowingChart,
        name: translation.trc(
          'bidmanager',
          'Maximum Sales on Top Positions'
        ),
        description: translation.trc(
          'bidmanager',
          'You will be shown in the top positions in search results to achieve the maximum number of sales'
        ),
        descriptionSeller: 'Товар окажется на лучших позициях в поиске по самому целевому кластеру'
      },
      BUDGET_SAVING_WB_AD_MANUAL: {
        label: {text: translation.trc('bidmanager', 'RECOMMENDED'), order: 0},
        icon: uniformDistribution,
        name: translation.trc(
          'bidmanager',
          'Budget Saving'
        ),
        description: translation.trc(
          'bidmanager',
          'You will be shown in optimal positions in search results to get the maximum orders with a limited budget'
        ),
        descriptionSeller: 'Реклама принесёт достаточно заказов, но не будет тратить бюджет слишком быстро'
      },
      BUDGET_SAVING_WB_AD_UNIFIED: {
        label: {text: translation.trc('bidmanager', 'RECOMMENDED'), order: 0},
        icon: uniformDistribution,
        name: translation.trc(
          'bidmanager',
          'Budget Saving'
        ),
        description: translation.trc(
          'bidmanager',
          'You will be shown in optimal positions in search results to get the maximum orders with a limited budget'
        ),
        descriptionSeller: 'Реклама принесёт достаточно заказов, но не будет тратить бюджет слишком быстро'
      },
      FIXED_BID_WB_AD_MANUAL: {
        icon: iconPointerClick,
        name: translation.trc(
          'bidmanager',
          'Fixed Bid'
        ),
        description: translation.trc(
          'bidmanager',
          'Products will be displayed in available positions based on the specified fixed bid'
        ),
        descriptionSeller: 'Товар окажется на тех позициях, которые доступны по вашей ставке'
      },
      FIXED_BID_WB_AD_UNIFIED: {
        icon: iconPointerClick,
        name: translation.trc(
          'bidmanager',
          'Fixed Bid'
        ),
        description: translation.trc(
          'bidmanager',
          'Products will be displayed in available positions based on the specified fixed bid'
        ),
        descriptionSeller: 'Товар окажется на тех позициях, которые доступны по вашей ставке'
      },
      HOLD_TARGET_CRR_WB_AD_MANUAL: {
        icon: iconCallToAction,
        nameSeller: 'Удержание ДРР',
        name: translation.trc(
          'bidmanager',
          'Maintaining target CRR'
        ),
        description: translation.trc(
          'bidmanager',
          'You will receive the maximum number of orders while taking into account CRR you set'
        ),
        descriptionSeller: 'Реклама будет лучше окупаться из-за перераспределения бюджета на более рентабельные товары'
      },
      HOLD_TARGET_CRR_WB_AD_UNIFIED: {
        icon: iconCallToAction,
        nameSeller: 'Удержание ДРР',
        name: translation.trc(
          'bidmanager',
          'Maintaining target CRR'
        ),
        description: translation.trc(
          'bidmanager',
          'You will receive the maximum number of orders while taking into account CRR you set'
        ),
        descriptionSeller: 'Реклама будет лучше окупаться из-за перераспределения бюджета на более рентабельные товары'
      },
      MAXIMUM_ORDERS_OZON_PAY_PER_CLICK_AD: {
        icon: iconTarget,
        nameSeller: 'Первые заказы',
        name: translation.trc(
          'bidmanager',
          'First orders'
        ),
        label: {text: translation.trc('bidmanager', 'RECOMMENDED'), order: 0},
        description: translation.trc(
          'bidmanager',
          'Orders will appear due to high bids, while the DRR can reach 30% at the start'
        ),
        descriptionSeller: 'Заказы появятся за счёт высоких ставок, при этом ДРР может достигать 30% на старте'
      },
      COMPETITIVE_BIDDING_OZON_PAY_PER_CLICK_AD: {
        icon: iconPointerClick,
        nameSeller: 'Конкурентные ставки',
        name: translation.trc(
          'bidmanager',
          'Competitive Bidding'
        ),
        description: translation.trc(
          'bidmanager',
          'Your products will receive maximum views by maintaining competitive bidding rates'
        ),
        descriptionSeller: 'Ставки будут меняться так, чтобы ваш товар показывался в поиске чаще, чем конкуренты'
      },
      TRMCORE_HOLD_TARGET_CRR_OZON_PAY_PER_CLICK_AD: {
        label: {text: translation.trc('bidmanager', 'RECOMMENDED'), order: 0},
        icon: iconCallToAction,
        name: translation.trc(
          'bidmanager',
          'Hold target CRR'
        ),
        description: translation.trc(
          'bidmanager',
          'You will receive a maximum number of orders, taking into account CRR you set'
        )
      },
      TRMCORE_HOLD_TARGET_ROI_OZON_PAY_PER_CLICK_AD: {
        icon: iconCursorClick,
        name: translation.trc(
          'bidmanager',
          'Optimization for targeted ROI'
        ),
        description: translation.trc(
          'bidmanager',
          'You will improve advertising return on investment within specified constraints'
        )
      },
      TRMCORE_INCREASE_ROI_HOLD_CPO_OZON_PAY_PER_CLICK_AD: {
        icon: iconGrowingChart,
        name: translation.trc(
          'bidmanager',
          'Increasing ROI while maintaining CPO'
        ),
        description: translation.trc(
          'bidmanager',
          'You will receive the maximum number of orders for profitable goods and reduce costs on goods with ' +
          'a high CPO'
        )
      },
      TRMCORE_MAXIMUM_ORDERS_OZON_PAY_PER_CLICK_AD: {
        icon: iconTarget,
        name: translation.trc(
          'bidmanager',
          'First orders'
        ),
        label: {text: translation.trc('bidmanager', 'RECOMMENDED'), order: 0},
        description: translation.trc(
          'bidmanager',
          'Orders will appear due to high bids, while the DRR can reach 30% at the start'
        )
      },
      TRMCORE_COMPETITIVE_BIDDING_OZON_PAY_PER_CLICK_AD: {
        icon: iconPointerClick,
        name: translation.trc(
          'bidmanager',
          'Competitive Bidding'
        ),
        description: translation.trc(
          'bidmanager',
          'Your products will receive maximum views by maintaining competitive bidding rates'
        )
      },
      SELLERS_HOLD_TARGET_CRR_OZON_PAY_PER_CLICK_AD: {
        label: {text: translation.trc('bidmanager', 'RECOMMENDED'), order: 0},
        icon: iconCallToAction,
        nameSeller: 'Удержание ДРР',
        name: translation.trc(
          'bidmanager',
          'Hold target CRR'
        ),
        description: translation.trc(
          'bidmanager',
          'You will receive a maximum number of orders, taking into account CRR you set'
        ),
        descriptionSeller: 'Реклама будет лучше окупаться из-за перераспределения бюджета на более рентабельные товары'
      },
      SELLERS_HOLD_TARGET_ROI_OZON_PAY_PER_CLICK_AD: {
        icon: iconCursorClick,
        name: translation.trc(
          'bidmanager',
          'Optimization for targeted ROI'
        ),
        nameSeller: 'Оптимизация по ROI',
        description: translation.trc(
          'bidmanager',
          'You will improve advertising return on investment within specified constraints'
        ),
        descriptionSeller: 'Получите максимум заказов c учётом желаемого показателя ROI'
      },
      SELLERS_INCREASE_ROI_HOLD_CPO_OZON_PAY_PER_CLICK_AD: {
        icon: iconGrowingChart,
        name: translation.trc(
          'bidmanager',
          'Increasing ROI while maintaining CPO'
        ),
        nameSeller: 'Удержание СРО',
        description: translation.trc(
          'bidmanager',
          'You will receive the maximum number of orders for profitable goods and reduce costs on goods with ' +
          'a high CPO'
        ),
        descriptionSeller: 'Получите максимум заказов c учётом желаемого показателя CPO'
      },
      SELLERS_HOLD_TARGET_POSITION_WB_AD_MANUAL: {
        icon: iconCursorClick,
        name: translation.trc(
          'bidmanager',
          'Hold target position'
        ),
        description: translation.trc(
          'bidmanager',
          'You will get the maximum number of impressions within the specified range'
        ),
        descriptionSeller: 'Товар получит максимальное количество показов в желаемом диапазоне позиций'
      },
      SELLERS_HOLD_TARGET_POSITION_WB_AD_UNIFIED: {
        icon: iconCursorClick,
        name: translation.trc(
          'bidmanager',
          'Hold target position'
        ),
        description: translation.trc(
          'bidmanager',
          'You will get the maximum number of impressions within the specified range'
        ),
        descriptionSeller: 'Товар получит максимальное количество показов в желаемом диапазоне позиций'
      },
      SELLERS_MAXIMUM_SALES_ON_TOP_POSITIONS_WB_AD_MANUAL: {
        label: {text: translation.trc('bidmanager', 'RECOMMENDED'), order: 0},
        icon: iconGrowingChart,
        name: translation.trc(
          'bidmanager',
          'Maximum Sales on Top Positions'
        ),
        description: translation.trc(
          'bidmanager',
          'You will be shown in the top positions in search results to achieve the maximum number of sales'
        ),
        descriptionSeller: 'Товар окажется на лучших позициях в поиске по самому целевому кластеру'
      },
      SELLERS_MAXIMUM_SALES_ON_TOP_POSITIONS_WB_AD_UNIFIED: {
        label: {text: translation.trc('bidmanager', 'RECOMMENDED'), order: 0},
        icon: iconGrowingChart,
        name: translation.trc(
          'bidmanager',
          'Maximum Sales on Top Positions'
        ),
        description: translation.trc(
          'bidmanager',
          'You will be shown in the top positions in search results to achieve the maximum number of sales'
        ),
        descriptionSeller: 'Товар окажется на лучших позициях в поиске по самому целевому кластеру'
      },
      SELLERS_BUDGET_SAVING_WB_AD_MANUAL: {
        label: {text: translation.trc('bidmanager', 'RECOMMENDED'), order: 0},
        icon: uniformDistribution,
        name: translation.trc(
          'bidmanager',
          'Budget Saving'
        ),
        description: translation.trc(
          'bidmanager',
          'You will be shown in optimal positions in search results to get the maximum orders with a limited budget'
        ),
        descriptionSeller: 'Реклама принесёт достаточно заказов, но не будет тратить бюджет слишком быстро'
      },
      SELLERS_BUDGET_SAVING_WB_AD_UNIFIED: {
        label: {text: translation.trc('bidmanager', 'RECOMMENDED'), order: 0},
        icon: uniformDistribution,
        name: translation.trc(
          'bidmanager',
          'Budget Saving'
        ),
        description: translation.trc(
          'bidmanager',
          'You will be shown in optimal positions in search results to get the maximum orders with a limited budget'
        ),
        descriptionSeller: 'Реклама принесёт достаточно заказов, но не будет тратить бюджет слишком быстро'
      },
      SELLERS_FIXED_BID_WB_AD_MANUAL: {
        icon: iconPointerClick,
        name: translation.trc(
          'bidmanager',
          'Fixed Bid'
        ),
        description: translation.trc(
          'bidmanager',
          'Products will be displayed in available positions based on the specified fixed bid'
        ),
        descriptionSeller: 'Товар окажется на тех позициях, которые доступны по вашей ставке'
      },
      SELLERS_FIXED_BID_WB_AD_UNIFIED: {
        icon: iconPointerClick,
        name: translation.trc(
          'bidmanager',
          'Fixed Bid'
        ),
        description: translation.trc(
          'bidmanager',
          'Products will be displayed in available positions based on the specified fixed bid'
        ),
        descriptionSeller: 'Товар окажется на тех позициях, которые доступны по вашей ставке'
      },
      SELLERS_HOLD_TARGET_CRR_WB_AD_MANUAL: {
        icon: iconCallToAction,
        nameSeller: 'Удержание ДРР',
        name: translation.trc(
          'bidmanager',
          'Maintaining target CRR'
        ),
        description: translation.trc(
          'bidmanager',
          'You will receive the maximum number of orders while taking into account CRR you set'
        ),
        descriptionSeller: 'Реклама будет лучше окупаться из-за перераспределения бюджета на более рентабельные товары'
      },
      SELLERS_HOLD_TARGET_CRR_WB_AD_UNIFIED: {
        icon: iconCallToAction,
        nameSeller: 'Удержание ДРР',
        name: translation.trc(
          'bidmanager',
          'Maintaining target CRR'
        ),
        description: translation.trc(
          'bidmanager',
          'You will receive the maximum number of orders while taking into account CRR you set'
        ),
        descriptionSeller: 'Реклама будет лучше окупаться из-за перераспределения бюджета на более рентабельные товары'
      },
      SELLERS_MAXIMUM_ORDERS_OZON_PAY_PER_CLICK_AD: {
        icon: iconTarget,
        nameSeller: 'Первые заказы',
        name: translation.trc(
          'bidmanager',
          'First orders'
        ),
        label: {text: translation.trc('bidmanager', 'RECOMMENDED'), order: 0},
        description: translation.trc(
          'bidmanager',
          'Orders will appear due to high bids, while the DRR can reach 30% at the start'
        ),
        descriptionSeller: 'Заказы появятся за счёт высоких ставок, при этом ДРР может достигать 30% на старте'
      },
      SELLERS_COMPETITIVE_BIDDING_OZON_PAY_PER_CLICK_AD: {
        icon: iconPointerClick,
        nameSeller: 'Конкурентные ставки',
        name: translation.trc(
          'bidmanager',
          'Competitive Bidding'
        ),
        description: translation.trc(
          'bidmanager',
          'Your products will receive maximum views by maintaining competitive bidding rates'
        ),
        descriptionSeller: 'Ставки будут меняться так, чтобы ваш товар показывался в поиске чаще, чем конкуренты'
      }
    };
  }

  _autostrategiesAliases () {
    return {
      DECREASE_GOAL_CPA_YANDEX: 'DECREASE_GOAL_CPA',
      MAX_TRAFFIC_HOLD_CPC_YANDEX: 'MAX_TRAFFIC_HOLD_CPC',
      INCREASE_GOAL_COMPLETIONS_YANDEX: 'INCREASE_GOAL_COMPLETIONS',
      DECREASE_GOAL_CPA_GOOGLE: 'DECREASE_GOAL_CPA',
      MAX_TRAFFIC_HOLD_CPC_GOOGLE: 'MAX_TRAFFIC_HOLD_CPC',
      INCREASE_GOAL_COMPLETIONS_GOOGLE: 'INCREASE_GOAL_COMPLETIONS',
      DECREASE_JOIN_RATE_COST_VKONTAKTE: 'DECREASE_JOIN_RATE_COST_VKONTAKTE',
      UNIFORM_DAY_BUDGET_SOCIAL_CAMPAIGN: 'UNIFORM_DAY_BUDGET',
      UNIFORM_DAY_BUDGET_SOCIAL_AD: 'UNIFORM_DAY_BUDGET',
      UNIFORM_DAY_BUDGET_CONTEXT_ADGROUP: 'UNIFORM_DAY_BUDGET',
      UNIFORM_DAY_BUDGET_CONTEXT_KEYWORD: 'UNIFORM_DAY_BUDGET',

      /*
       * Вариант без оптимизации показывается пользователю как та же стратегия «Конкурентные ставки»,
       * поэтому название, описание и иконку он получает через metaId парного варианта.
       */
      ...COMPETITIVE_BIDDING_NO_OPTIMIZATION_ALIASES
    };
  }

  _enrichAutostrategy (autostrategy) {
    const metaId = this._autostrategiesAliases()[autostrategy.id] || autostrategy.id;
    const dataForEnrich = this._autostrategiesDataForEnrich()[metaId];
    return {...autostrategy, ...dataForEnrich, metaId};
  }

  getAutostrategyById (autostrategyAllias) {
    return this._autostrategiesDataForEnrich()[autostrategyAllias];
  }

  async list (projectId) {
    // TODO: this method must be called with projectId
    const url = BID_MANAGER_AUTOSTRATEGIES_URL.format(projectId);
    const data = await this.client.get(url);
    return data.items.map(autostrategy => this._enrichAutostrategy(autostrategy));
  }
}
